import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2, Mail } from "lucide-react";

const SERVICES = [
  "Partnerships & Sponsorships",
  "Video Production & Creative",
  "Marketing Strategy & Growth",
  "Account & Operations",
  "Digital Marketing & Performance",
  "Technology & Development",
  "General Enquiry",
];

const BUDGETS = [
  "Under ₹2L",
  "₹2L – ₹10L",
  "₹10L – ₹50L",
  "₹50L+",
  "Not sure yet",
];

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const CONTACT_PATH = process.env.REACT_APP_CONTACT_PATH || "/api/contact";
const CONTACT_URL = `${API_BASE}${CONTACT_PATH}`;

export const ContactDialog = ({ open, onOpenChange, prefill }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Sync prefill whenever dialog opens
  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        service: prefill?.service || f.service || "",
      }));
      setSuccess(false);
      setErrors({});
    }
  }, [open, prefill]);

  const update = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.service) e.service = "Pick a service";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Tell us a little more (min 10 characters)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(CONTACT_URL, {
        ...form,
        source: prefill?.source || "landing-page",
        submitted_at: new Date().toISOString(),
      });
      setSuccess(true);
      toast.success("Message received", {
        description: "We'll be in touch within 24 hours.",
      });
    } catch (err) {
      toast.error("Could not submit", {
        description: err?.response?.data?.detail || "Please try again shortly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      company: "",
      service: "",
      budget: "",
      message: "",
    });
    setSuccess(false);
    setErrors({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-dialog"
        className="bg-[#0a0a0c] border-white/10 text-white max-w-2xl p-0 gap-0 sm:max-h-[min(90dvh,900px)]"
      >
        <div className="relative">
          {/* Magenta accent header strip */}
          <div className="h-1 w-full bg-gradient-to-r from-fuchsia-600 via-fuchsia-400 to-fuchsia-700" />

          <div className="p-5 sm:p-7 md:p-9 pt-12 sm:pt-9">
            {!success ? (
              <>
                <DialogHeader className="text-left space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="h-px w-8 bg-fuchsia-500" />
                    <span className="text-[10px] uppercase tracking-[0.32em] text-fuchsia-400 font-bold">
                      Let&apos;s talk
                    </span>
                  </div>
                  <DialogTitle className="font-display text-xl sm:text-2xl md:text-3xl font-black tracking-tight leading-tight pr-8">
                    Tell us about your{" "}
                    <span className="text-fuchsia-500">brand goals.</span>
                  </DialogTitle>
                  <DialogDescription className="text-sm text-white/55 font-body">
                    Share a few details and we&apos;ll get back to you within 24 hours
                    with a tailored plan.
                  </DialogDescription>
                </DialogHeader>

                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                      label="Full name"
                      required
                      error={errors.name}
                      htmlFor="contact-name"
                    >
                      <Input
                        id="contact-name"
                        data-testid="contact-input-name"
                        placeholder="Jane Doe"
                        value={form.name}
                        onChange={update("name")}
                        className="bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-fuchsia-500 focus-visible:ring-offset-0 h-11 rounded-none text-base"
                      />
                    </Field>
                    <Field
                      label="Email"
                      required
                      error={errors.email}
                      htmlFor="contact-email"
                    >
                      <Input
                        id="contact-email"
                        type="email"
                        data-testid="contact-input-email"
                        placeholder="jane@brand.com"
                        value={form.email}
                        onChange={update("email")}
                        className="bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-fuchsia-500 focus-visible:ring-offset-0 h-11 rounded-none text-base"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Phone" htmlFor="contact-phone">
                      <Input
                        id="contact-phone"
                        data-testid="contact-input-phone"
                        placeholder="+91 98xxxxxxxx"
                        value={form.phone}
                        onChange={update("phone")}
                        className="bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-fuchsia-500 focus-visible:ring-offset-0 h-11 rounded-none text-base"
                      />
                    </Field>
                    <Field label="Company" htmlFor="contact-company">
                      <Input
                        id="contact-company"
                        data-testid="contact-input-company"
                        placeholder="Your brand / company"
                        value={form.company}
                        onChange={update("company")}
                        className="bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-fuchsia-500 focus-visible:ring-offset-0 h-11 rounded-none text-base"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                      label="Service of interest"
                      required
                      error={errors.service}
                    >
                      <Select value={form.service} onValueChange={update("service")}>
                        <SelectTrigger
                          data-testid="contact-select-service"
                          className="bg-white/[0.03] border-white/15 text-white h-11 rounded-none focus:ring-fuchsia-500 focus:ring-offset-0 data-[placeholder]:text-white/30 text-base"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          sideOffset={4}
                          className="bg-[#0a0a0c] border-white/15 text-white max-h-[min(240px,45dvh)]"
                        >
                          {SERVICES.map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              className="focus:bg-fuchsia-500/15 focus:text-white"
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Budget (optional)">
                      <Select value={form.budget} onValueChange={update("budget")}>
                        <SelectTrigger
                          data-testid="contact-select-budget"
                          className="bg-white/[0.03] border-white/15 text-white h-11 rounded-none focus:ring-fuchsia-500 focus:ring-offset-0 data-[placeholder]:text-white/30 text-base"
                        >
                          <SelectValue placeholder="Select a range" />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          sideOffset={4}
                          className="bg-[#0a0a0c] border-white/15 text-white max-h-[min(240px,45dvh)]"
                        >
                          {BUDGETS.map((b) => (
                            <SelectItem
                              key={b}
                              value={b}
                              className="focus:bg-fuchsia-500/15 focus:text-white"
                            >
                              {b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field
                    label="How can we help?"
                    required
                    error={errors.message}
                    htmlFor="contact-message"
                  >
                    <Textarea
                      id="contact-message"
                      data-testid="contact-input-message"
                      placeholder="Tell us about your goals, timelines, audience…"
                      rows={4}
                      value={form.message}
                      onChange={update("message")}
                      className="bg-white/[0.03] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-fuchsia-500 focus-visible:ring-offset-0 rounded-none resize-none text-base min-h-[120px]"
                    />
                  </Field>

                  <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                    <p className="text-[11px] text-white/40 flex items-center justify-center sm:justify-start gap-2 text-center sm:text-left">
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      We typically reply within 24 hours.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit"
                      className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-3.5 rounded-full transition-all duration-300 ember-glow"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          Send Enquiry
                          <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <SuccessState onClose={resetAndClose} name={form.name} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, htmlFor, required, error, children }) => (
  <div className="space-y-1.5">
    <Label
      htmlFor={htmlFor}
      className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold"
    >
      {label}
      {required && <span className="text-fuchsia-400 ml-1">*</span>}
    </Label>
    {children}
    {error && (
      <p className="text-xs text-fuchsia-400 mt-1" data-testid="contact-field-error">
        {error}
      </p>
    )}
  </div>
);

const SuccessState = ({ onClose, name }) => (
  <div className="text-center py-6 sm:py-8 px-1" data-testid="contact-success">
    <div className="mx-auto w-14 h-14 flex items-center justify-center bg-fuchsia-500/15 border border-fuchsia-500/40 rounded-full mb-5">
      <CheckCircle2 className="w-7 h-7 text-fuchsia-400" />
    </div>
    <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-black tracking-tight leading-tight px-2">
      Thanks{name ? `, ${name.split(" ")[0]}` : ""}.{" "}
      <span className="text-fuchsia-500">We&apos;ll be in touch.</span>
    </h3>
    <p className="mt-3 text-sm text-white/60 max-w-md mx-auto">
      Our team will review your enquiry and reach out within 24 hours to set up
      the next conversation.
    </p>
    <button
      onClick={onClose}
      data-testid="contact-success-close"
      className="mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-2 border border-white/20 hover:border-fuchsia-400 hover:bg-white/5 text-white font-semibold px-6 py-3 rounded-full transition-all text-sm"
    >
      Close
    </button>
  </div>
);
